import { useState, useEffect } from 'react';
import AdminLayout from './AdminLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { admin, getErrorMessage } from '../../utils/api';
import { toast } from 'sonner';
import { Trash2, RotateCcw, Loader2, ShieldCheck, Store } from 'lucide-react';

const REASON_LABEL = {
  seed_email: 'Seed (@dealshaq.test)',
  test_name_pattern: 'Test name',
  hidden_demo_name: 'Out-of-market demo',
};

export default function AdminSeedCleanup({ user, onLogout }) {
  const [data, setData] = useState({ active: [], archived: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [view, setView] = useState('active');
  const [selected, setSelected] = useState(new Set());

  const load = async () => {
    setLoading(true);
    try {
      const res = await admin.seedCleanupCandidates();
      setData(res.data);
      // default: select all in the active view
      setSelected(new Set((res.data.active || []).map((c) => c.id)));
    } catch (e) {
      toast.error(getErrorMessage(e, 'Failed to load candidates'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const rows = view === 'active' ? data.active : data.archived;

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selected.size === rows.length) setSelected(new Set());
    else setSelected(new Set(rows.map((r) => r.id)));
  };

  const switchView = (v) => {
    setView(v);
    const list = v === 'active' ? data.active : data.archived;
    setSelected(new Set(list.map((c) => c.id)));
  };

  const doArchive = async () => {
    const ids = [...selected];
    if (!ids.length) return toast.error('Select at least one store');
    if (!window.confirm(`Archive ${ids.length} dummy store(s)? They'll be hidden from dashboards & directory. This is reversible.`)) return;
    setBusy(true);
    try {
      const res = await admin.seedCleanupArchive(ids);
      toast.success(`Archived ${res.data.archived} store(s)${res.data.skipped ? `, skipped ${res.data.skipped}` : ''}`);
      await load();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Archive failed'));
    } finally { setBusy(false); }
  };

  const doRestore = async () => {
    const ids = [...selected];
    if (!ids.length) return toast.error('Select at least one store');
    setBusy(true);
    try {
      const res = await admin.seedCleanupRestore(ids);
      toast.success(`Restored ${res.data.restored} store(s)`);
      await load();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Restore failed'));
    } finally { setBusy(false); }
  };

  return (
    <AdminLayout user={user} onLogout={onLogout}>
      <div className="space-y-6" data-testid="admin-seed-cleanup">
        <div className="bg-gradient-to-r from-slate-700 to-slate-900 rounded-2xl p-8 text-white shadow-lg">
          <h2 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>Seed Cleanup</h2>
          <p className="text-slate-200 text-lg">Safely soft-archive dummy / seed retailer accounts so pilot dashboards show only real stores. Reversible — nothing is deleted.</p>
        </div>

        <div className="flex gap-2">
          <Button variant={view === 'active' ? 'default' : 'outline'} onClick={() => switchView('active')} data-testid="seed-view-active">
            Detected ({data.active.length})
          </Button>
          <Button variant={view === 'archived' ? 'default' : 'outline'} onClick={() => switchView('archived')} data-testid="seed-view-archived">
            Archived ({data.archived.length})
          </Button>
          <div className="flex-1" />
          {view === 'active' ? (
            <Button className="bg-red-600 hover:bg-red-700" onClick={doArchive} disabled={busy || rows.length === 0} data-testid="seed-archive-btn">
              {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Trash2 className="w-4 h-4 mr-1" />}
              Archive Selected ({selected.size})
            </Button>
          ) : (
            <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={doRestore} disabled={busy || rows.length === 0} data-testid="seed-restore-btn">
              {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RotateCcw className="w-4 h-4 mr-1" />}
              Restore Selected ({selected.size})
            </Button>
          )}
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Store className="w-5 h-5 text-slate-600" />
              {view === 'active' ? 'Detected dummy stores' : 'Archived stores'}
            </CardTitle>
            <CardDescription>
              Review the list before acting. "Orders" shows how many real orders reference the account (a safety signal).
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <p className="text-center text-gray-500 py-8">Loading…</p>
            ) : rows.length === 0 ? (
              <div className="text-center py-10">
                <ShieldCheck className="w-12 h-12 text-green-500 mx-auto mb-3" />
                <p className="text-gray-500">{view === 'active' ? 'No dummy stores detected. Dashboards are clean.' : 'Nothing archived yet.'}</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-gray-500 border-b">
                      <th className="py-2 pr-2">
                        <input type="checkbox" checked={selected.size === rows.length && rows.length > 0} onChange={toggleAll} data-testid="seed-select-all" />
                      </th>
                      <th className="py-2 pr-4">Store</th>
                      <th className="py-2 pr-4">Email</th>
                      <th className="py-2 pr-4">Reason</th>
                      <th className="py-2 pr-4">Status</th>
                      <th className="py-2 pr-4 text-right">Orders</th>
                      <th className="py-2 pr-4 text-right">Items</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr key={r.id} className="border-b hover:bg-gray-50" data-testid={`seed-row-${r.id}`}>
                        <td className="py-2 pr-2">
                          <input type="checkbox" checked={selected.has(r.id)} onChange={() => toggle(r.id)} data-testid={`seed-check-${r.id}`} />
                        </td>
                        <td className="py-2 pr-4 font-medium text-gray-900">{r.store_name || r.name}</td>
                        <td className="py-2 pr-4 text-gray-600">{r.email}</td>
                        <td className="py-2 pr-4">
                          <Badge variant="outline" className="text-xs">{REASON_LABEL[r.reason] || r.reason}</Badge>
                        </td>
                        <td className="py-2 pr-4">
                          <Badge className={r.archived ? 'bg-gray-400' : 'bg-slate-600'}>{r.store_status}</Badge>
                        </td>
                        <td className={`py-2 pr-4 text-right ${r.order_count > 0 ? 'text-amber-600 font-semibold' : 'text-gray-500'}`}>{r.order_count}</td>
                        <td className="py-2 pr-4 text-right text-gray-500">{r.items_count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
